/**
 * LaTeX kaynak çıkarımı.
 *
 * .tex dosyaları PDF'e göre çok daha temizdir: bölüm yapısı, tablo/şekil
 * başlıkları ve kaynakça açıkça işaretlidir. Bu yüzden LaTeX'te yapısal
 * denetimler daha yüksek güvenle çalışır. Sayfa/bbox yoktur; satır numarası
 * konum olarak saklanır.
 */

import type { TextBlock } from '../types';
import { normalizeText } from '../text/normalize';

export interface LatexExtraction {
	blocks: TextBlock[];
	meta: { title?: string; author?: string };
	warnings: string[];
}

interface Section {
	title: string;
	level: number;
	line: number;
	body: string;
}

const SECTION_RE = /^\s*\\(part|chapter|section|subsection|subsubsection)\*?\s*(?:\[[^\]]*\])?\s*\{([^}]*)\}/;

export function extractLatex(source: string): LatexExtraction {
	const warnings: string[] = [];
	const lines = source.split(/\r?\n/);
	const meta: { title?: string; author?: string } = {};

	// Preamble meta
	const titleMatch = source.match(/\\title\s*(?:\[[^\]]*\])?\s*\{([\s\S]*?)\}\s*(?:\n|$)/);
	if (titleMatch) meta.title = plainText(titleMatch[1]).trim();
	const authorMatch = source.match(/\\author\s*(?:\[[^\]]*\])?\s*\{([\s\S]*?)\}\s*(?:\n|$)/);
	if (authorMatch) meta.author = plainText(authorMatch[1]).trim();

	// Ön bölüm: \begin{document} sonrası, ilk \section veya abstract
	let bodyStart = 0;
	const docStart = lines.findIndex((l) => /\\begin\s*\{document\}/.test(l));
	if (docStart >= 0) bodyStart = docStart + 1;
	else warnings.push('\\begin{document} bulunamadı; tüm dosya gövde olarak işlendi.');

	const sections: Section[] = [];
	let current: Section = { title: '', level: 0, line: bodyStart + 1, body: '' };
	let inCommentBlock = false;
	let abstractSeen = false;
	let inAbstract = false;
	let inThebibliography = false;
	const preambleChunks: string[] = [];

	for (let i = bodyStart; i < lines.length; i++) {
		const rawLine = lines[i];

		// Yorumlar
		const commentIdx = findComment(rawLine);
		let line = commentIdx >= 0 ? rawLine.slice(0, commentIdx) : rawLine;
		if (inCommentBlock) {
			if (line.includes('\\end{comment}')) {
				inCommentBlock = false;
				line = line.slice(line.indexOf('\\end{comment}') + 12);
			} else {
				continue;
			}
		}
		if (line.includes('\\begin{comment}')) {
			inCommentBlock = true;
			continue;
		}

		if (/\\begin\s*\{abstract\}/.test(line)) {
			inAbstract = true;
			abstractSeen = true;
			if (current.title === '') {
				// ön bölümü abstract'a bağla
			}
			continue;
		}
		if (/\\end\s*\{abstract\}/.test(line)) {
			inAbstract = false;
			continue;
		}
		if (/\\begin\s*\{thebibliography\}/.test(line)) {
			inThebibliography = true;
			if (current.body.trim()) sections.push(current);
			current = { title: 'Kaynakça', level: 1, line: i + 1, body: '' };
			continue;
		}
		if (/\\end\s*\{thebibliography\}/.test(line)) {
			inThebibliography = false;
			sections.push(current);
			current = { title: '', level: 0, line: i + 1, body: '' };
			continue;
		}

		const sec = SECTION_RE.exec(line);
		if (sec) {
			if (current.body.trim()) sections.push(current);
			const title = plainText(sec[2]).trim();
			current = { title, level: levelOf(sec[1]), line: i + 1, body: '' };
			continue;
		}

		if (/\\end\s*\{document\}/.test(line)) break;

		// Bibitem → kaynakça girdisi
		if (inThebibliography && /\\bibitem/.test(line)) {
			current.body += '\n';
		}

		const converted = convertLatexLine(line, { inAbstract, inThebibliography });
		if (converted.trim()) {
			if (current.title === '' && !abstractSeen) preambleChunks.push(converted);
			else current.body += converted + '\n';
		}
	}
	if (current.body.trim()) sections.push(current);

	for (const chunk of preambleChunks) {
		if (!sections.some((s) => s.title === 'Özet')) {
			sections.unshift({ title: 'Özet', level: 1, line: bodyStart + 1, body: chunk });
		}
	}

	// Başlık yoksa ve gövde varsa tek blok
	if (sections.length === 0 && source.trim()) {
		sections.push({ title: '', level: 0, line: 1, body: stripPreamble(source) });
	}

	const blocks: TextBlock[] = [];
	let idx = 0;
	for (const s of sections) {
		const body = collapse(s.body).trim();
		if (!body) continue;
		const rawText = s.title ? `${s.title}\n${body}` : body;
		const normalized = normalizeText(rawText);
		blocks.push({
			id: `tex-s${idx++}`,
			lineStart: s.line,
			lineEnd: s.line + (s.body.match(/\n/g)?.length ?? 0),
			raw: rawText,
			text: normalized.text,
			offsetMap: normalized.map
		});
	}

	if (!blocks.some((b) => /kaynak|referans|bibliograph/i.test(b.text))) {
		warnings.push('Kaynakça ortamı (thebibliography) bulunamadı; atıf denetimi sınırlı çalışacak.');
	}

	return { blocks, meta, warnings };
}

function levelOf(kind: string): number {
	if (kind === 'part' || kind === 'chapter') return 1;
	if (kind === 'section') return 1;
	if (kind === 'subsection') return 2;
	return 3;
}

function findComment(line: string): number {
	for (let i = 0; i < line.length; i++) {
		if (line[i] === '%' && (i === 0 || line[i - 1] !== '\\')) return i;
	}
	return -1;
}

function collapse(s: string): string {
	return s.replace(/[ \t]+/g, ' ').replace(/\n{3,}/g, '\n\n');
}

/** LaTeX satırını okunabilir düz metne çevirir. */
function convertLatexLine(
	line: string,
	ctx: { inAbstract: boolean; inThebibliography: boolean }
): string {
	let s = line;

	// Sık kullanılan komutlar
	s = s.replace(/\\(?:begin|end)\s*\{[^}]*\}/g, ' ');
	s = s.replace(/\\(?:label|index|footnote|thanks|vspace|hspace|smallskip|medskip|bigskip)\*?\s*(?:\[[^\]]*\])?\s*/g, ' ');
	s = s.replace(/\\(?:noindent|centering|raggedright|newpage|clearpage|maketitle|tableofcontents)\b/g, ' ');
	s = s.replace(/\\(?:cite|citep|citet|parencite|textcite|autocite|supercite)\*?\s*(?:\[[^\]]*\]){0,2}\s*\{([^}]*)\}/g, (_m, keys: string) => {
		// \cite{a,b} → (a; b) — atıf motoru anahtarları eşleştirebilsin
		const parts = keys
			.split(',')
			.map((k) => k.trim())
			.filter(Boolean);
		return parts.length ? ` (${parts.join('; ')})` : '';
	});
	s = s.replace(/\\(?:ref|autoref|cref|Cref|pageref)\s*\{([^}]*)\}/g, (_m, key: string) => {
		const tab = /tab/i.test(key);
		const fig = /fig/i.test(key);
		const num = (key.match(/\d+/) ?? [''])[0];
		if (tab) return `Tablo ${num}`.trim();
		if (fig) return `Şekil ${num}`.trim();
		return key.replace(/[:_]/g, ' ');
	});
	s = s.replace(/\\caption\s*(?:\[[^\]]*\])?\s*\{([^}]*)\}/g, (_m, cap: string) => {
		const clean = plainText(cap);
		return `\n${clean}\n`;
	});
	s = s.replace(/\\bibitem\s*(?:\[[^\]]*\])?\s*\{([^}]*)\}/g, (_m, key: string) => `\n${key}\n`);
	s = s.replace(/\\(?:item|and)\b/g, ' ');
	s = s.replace(/\\%/g, '%');
	s = s.replace(/\\&/g, '&');
	s = s.replace(/\\_/g, '_');
	s = s.replace(/\\\$ /g, ' ');
	s = s.replace(/\\(?:textbf|textit|emph|underline|textrm|textsf|texttt|textsc|mbox|text|mathrm|mathbf|mathit)\s*\{([^{}]*)\}/g, '$1');

	// Matematik: satır içi $...$ ve $$...$$ içeriğini koru, sınırları at
	s = s.replace(/\$\$([^$]*)\$\$/g, ' $1 ');
	s = s.replace(/\$([^$]*)\$/g, ' $1 ');

	// Kalan komutlar: argümansız olanları at, argümanlıları düz metne çevir
	s = s.replace(/\\[a-zA-Z@]+\*?\s*(?:\[[^\]]*\])?\s*\{([^{}]*)\}/g, '$1');
	s = s.replace(/\\[a-zA-Z@]+\*?/g, ' ');
	s = s.replace(/[{}]/g, ' ');
	s = s.replace(/~/g, ' ');
	s = s.replace(/``/g, '"').replace(/''/g, '"');
	s = s.replace(/---/g, '—').replace(/--/g, '–');

	return s.replace(/[ \t]+/g, ' ').trim();
}

function plainText(s: string): string {
	return s
		.replace(/\\[a-zA-Z@]+\*?\s*\{([^{}]*)\}/g, '$1')
		.replace(/\\[a-zA-Z@]+\*?/g, ' ')
		.replace(/[{}]/g, '')
		.replace(/~|\\/g, ' ')
		.replace(/\s+/g, ' ')
		.trim();
}

function stripPreamble(source: string): string {
	const start = source.indexOf('\\begin{document}');
	return start >= 0 ? source.slice(start + 18) : source;
}
